import {scheduleStyleIntent} from '@sail/engine';
import {css, view, createView} from '@sail/ui';
import {
  css as deprecatedCss,
  view as deprecatedView,
} from '@sail/ui/deprecated';
import {useTheme} from '../../ThemeProvider';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {renderMarkdownInline} from '../../helpers/markdown';
import {secondaryLinkStyles} from '../../helpers/style-intents';
import {dotComColors} from '../../theme/dotCom';

// Note: this is only used in the old designs
const backgroundToFontColorMapping = {
  neutral100: 'primary',
  neutral600: deprecatedCss.value('white'),
} as const;

const dotComBackgroundToFontColorMapping = {
  neutral100: deprecatedCss.value(dotComColors.brand600),
  neutral600: deprecatedCss.value(dotComColors.brand),
} as const;

const className = 'uh-conversations-chat-text-body';
const chatSelector = `.${className}`;
const userMessage = `[data-message-type="user"] ${chatSelector}`;

const splashLinkStyles = css.global({
  [`${chatSelector}-splash a`]: {
    color: 'var(--link-color)',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    textDecorationThickness: '0.07em',
  },
  [`${userMessage}-splash a`]: {
    color: 'inherit',
  },
});

scheduleStyleIntent(splashLinkStyles);

export type Props = {
  body: string;
  background?: 'neutral100' | 'neutral600';
  textSize?: 'default' | 'small';
  css?: any;
};

export const TextBody = createView(
  ({
    body,
    background = 'neutral100',
    textSize = 'default',
    ...props
  }: Props) => {
    const useNewDesign = useNewUniversalHelpDesign();
    const useDotComDesign = useUniversalHelpDotComDesign();
    const theme = useTheme();

    if (useNewDesign) {
      // eslint-disable-next-line no-use-before-define
      return <TextBodyNext body={body} {...props} />;
    }

    return (
      <deprecatedView.div
        className={`${className}-splash`}
        css={{stack: 'y', gap: 'small', '--link-color': theme.linkColor}}
        {...props}
      >
        <deprecatedView.div>
          {body.split(/\r?\n/g).map((p, i) => (
            <deprecatedView.span
              // eslint-disable-next-line react/no-array-index-key
              key={`text-row-body-${i}`}
              css={{
                color: useDotComDesign
                  ? dotComBackgroundToFontColorMapping[background]
                  : backgroundToFontColorMapping[background],
                font: textSize === 'small' ? 'caption' : 'body',
              }}
            >
              <deprecatedView.span
                // Sanitize the html here so that we prevent any security risks
                //
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownInline(p),
                }}
              />
              <br />
            </deprecatedView.span>
          ))}
        </deprecatedView.div>
      </deprecatedView.div>
    );
  },
);

const TextBodyNext = createView(
  ({body, textSize = 'default', ...props}: Omit<Props, 'background'>) => {
    const theme = useTheme();

    return (
      <view.div
        className={className}
        css={{stack: 'y', ...props.css}}
        uses={[
          secondaryLinkStyles(chatSelector),
          css.global({
            [`${userMessage} a`]: {textColor: theme.messageBubbleText},
            [`${userMessage} a:hover`]: {
              textColor: theme.messageBubbleText,
            },
          }),
          textSize === 'small' && css({fontSize: 12, lineHeight: '1.4em'}),
        ]}
      >
        {body.split(/\r?\n/g).map((p, i) => {
          // Default to the zero-width space unicode character.
          // Otherwise an empty node won't take up a line and will
          // collapse to 0 height
          const rendered =
            renderMarkdownInline(p, {removeTargetInLinks: false}) || '\u200B';
          return (
            <view.span
              css={{
                wordBreak: 'break-word',
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`text-row-body-${i}`}
              // Sanitize the html here so that we prevent any security risks
              //
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{__html: rendered}}
            />
          );
        })}
      </view.div>
    );
  },
);
